import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@dashboard/hooks/useAuth';
import { ThemeContext, useThemeProvider } from '@dashboard/hooks/useTheme';

interface NavItem {
  to: string;
  symbol: string;
  label: string;
  end: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/04062001/admin', symbol: '\u25CE', label: 'Overview', end: true },
  { to: '/04062001/admin/applications', symbol: '\u229E', label: 'Applications', end: false },
  { to: '/04062001/admin/merchants', symbol: '\u03A3', label: 'Merchants', end: false },
  { to: '/04062001/admin/orders', symbol: '\u2295', label: 'Orders', end: false },
  { to: '/04062001/admin/revenue', symbol: '\u039E', label: 'Revenue', end: false },
  { to: '/04062001/admin/audit-log', symbol: '\u22A1', label: 'Audit Log', end: false },
];

function HeaderClock() {
  const [time, setTime] = useState('');

  useEffect(() => {
    function tick() {
      const now = new Date();
      const datePart = now.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
      const timePart = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      setTime(`${datePart}  ${timePart}`);
    }
    tick();
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="font-mono text-[11px] tabular-nums" style={{ color: 'var(--dash-text-dim)' }}>{time}</span>
  );
}

export default function AdminDashboardLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const themeCtx = useThemeProvider();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const displayEmail = user?.email || '';
  const sidebarWidth = collapsed ? 'w-[56px]' : 'w-52';

  return (
    <ThemeContext.Provider value={themeCtx}>
      <div
        className={`dash-theme ${themeCtx.theme === 'light' ? 'dash-light' : ''} flex h-screen overflow-hidden`}
        style={{ background: 'var(--dash-bg)' }}
      >
        <aside
          className={`${sidebarWidth} flex flex-col shrink-0 transition-all duration-200`}
          style={{ background: 'var(--dash-sidebar)', borderRight: '1px solid var(--dash-border)' }}
        >
          <div
            className={`${collapsed ? 'px-2' : 'px-4'} h-12 flex items-center justify-between`}
            style={{ borderBottom: '1px solid var(--dash-border)' }}
          >
            {!collapsed && (
              <div className="flex items-center gap-2">
                <span className="font-mono tracking-exhibit text-xs font-medium uppercase" style={{ color: 'var(--dash-text-primary)' }}>
                  BONESPAY
                </span>
                <span
                  className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                  style={{
                    color: 'var(--dash-text-dim)',
                    border: '1px solid var(--dash-border)',
                    background: 'var(--dash-hover-bg)',
                  }}
                >
                  Admin
                </span>
              </div>
            )}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="transition-colors p-1 rounded font-mono text-sm"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{ color: 'var(--dash-text-dim)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--dash-text-tertiary)';
                e.currentTarget.style.background = 'var(--dash-hover-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--dash-text-dim)';
                e.currentTarget.style.background = '';
              }}
            >
              {collapsed ? '\u203A' : '\u2039'}
            </button>
          </div>

          <nav className="flex-1 py-2 overflow-y-auto">
            {NAV_ITEMS.map(({ to, symbol, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                title={collapsed ? label : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 ${collapsed ? 'justify-center mx-2 px-2' : 'mx-2 px-3'} py-2 text-[13px] font-sans rounded transition-all duration-150 ${
                    isActive ? 'nav-active' : 'nav-inactive'
                  }`
                }
                style={({ isActive }) => ({
                  color: isActive ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
                  background: isActive ? 'var(--dash-active-bg)' : undefined,
                })}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  if (!el.classList.contains('nav-active')) {
                    el.style.color = 'var(--dash-text-secondary)';
                    el.style.background = 'var(--dash-hover-bg)';
                  }
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  if (!el.classList.contains('nav-active')) {
                    el.style.color = 'var(--dash-text-muted)';
                    el.style.background = '';
                  }
                }}
              >
                <span className="w-4 text-center font-mono text-[13px]">{symbol}</span>
                {!collapsed && label}
              </NavLink>
            ))}
          </nav>

          <div style={{ borderTop: '1px solid var(--dash-border)' }}>
            <NavLink
              to="/04062001/admin/settings"
              end={false}
              title={collapsed ? 'Settings' : undefined}
              className={({ isActive }) =>
                `flex items-center gap-2.5 ${collapsed ? 'justify-center mx-2 px-2' : 'mx-2 px-3'} py-2 mt-2 text-[13px] font-sans rounded transition-all duration-150 ${
                  isActive ? 'nav-active' : 'nav-inactive'
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
                background: isActive ? 'var(--dash-active-bg)' : undefined,
              })}
              onMouseEnter={(e) => {
                const el = e.currentTarget;
                if (!el.classList.contains('nav-active')) {
                  el.style.color = 'var(--dash-text-secondary)';
                  el.style.background = 'var(--dash-hover-bg)';
                }
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget;
                if (!el.classList.contains('nav-active')) {
                  el.style.color = 'var(--dash-text-muted)';
                  el.style.background = '';
                }
              }}
            >
              <span className="w-4 text-center font-mono text-[13px]">{'\u2699'}</span>
              {!collapsed && 'Settings'}
            </NavLink>

            {!collapsed && (
              <div
                className="mx-3 mb-2 mt-1 px-3 py-2 rounded"
                style={{
                  background: 'var(--dash-hover-bg)',
                  border: '1px solid var(--dash-border-light)',
                }}
              >
                <div className="font-sans text-[12px] truncate" style={{ color: 'var(--dash-text-tertiary)' }}>Administrator</div>
                <div className="font-mono text-[10px] truncate" style={{ color: 'var(--dash-text-faint)' }}>{displayEmail}</div>
              </div>
            )}
            {collapsed && (
              <div className="flex justify-center mb-2 mt-1">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  title="Administrator"
                  style={{
                    background: 'var(--dash-card)',
                    border: '1px solid var(--dash-border)',
                  }}
                >
                  <span className="font-mono text-[10px]" style={{ color: 'var(--dash-text-dim)' }}>A</span>
                </div>
              </div>
            )}
          </div>
        </aside>

        <div className="flex-1 flex flex-col overflow-hidden">
          <header
            className="h-12 flex items-center justify-between px-5 shrink-0"
            style={{ background: 'var(--dash-header)', borderBottom: '1px solid var(--dash-border)' }}
          >
            <HeaderClock />

            <div className="flex items-center gap-1">
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden transition-colors ml-1"
                  style={{
                    background: 'var(--dash-card)',
                    border: '1px solid var(--dash-border)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dash-text-faint)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dash-border)'; }}
                >
                  <span className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>A</span>
                </button>

                {profileOpen && (
                  <div
                    className="absolute right-0 top-full mt-1 w-48 rounded shadow-xl z-50 py-1"
                    style={{
                      background: 'var(--dash-card-hover)',
                      border: '1px solid var(--dash-border)',
                    }}
                  >
                    <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--dash-border)' }}>
                      <div className="font-sans text-[12px] truncate" style={{ color: 'var(--dash-text-tertiary)' }}>Administrator</div>
                      <div className="font-mono text-[10px] truncate" style={{ color: 'var(--dash-text-dim)' }}>{displayEmail}</div>
                    </div>
                    <button
                      onClick={() => { setProfileOpen(false); navigate('/04062001/admin/settings'); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-[13px] font-sans transition-colors"
                      style={{ color: 'var(--dash-text-muted)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--dash-text-secondary)';
                        e.currentTarget.style.background = 'var(--dash-hover-bg)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--dash-text-muted)';
                        e.currentTarget.style.background = '';
                      }}
                    >
                      <span className="w-3.5 text-center font-mono text-[11px]">{'\u2699'}</span>
                      Settings
                    </button>
                    <button
                      onClick={() => { setProfileOpen(false); handleSignOut(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-[13px] font-sans transition-colors"
                      style={{ color: 'var(--dash-text-muted)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#f87171';
                        e.currentTarget.style.background = 'var(--dash-hover-bg)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--dash-text-muted)';
                        e.currentTarget.style.background = '';
                      }}
                    >
                      <span className="w-3.5 text-center font-mono text-[12px]">{'\u23CF'}</span>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-auto" style={{ background: 'var(--dash-bg)' }}>
            <Outlet />
          </main>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}
